import { apiClient } from "./apiClient";
import {
  IPO,
  IpoGmpHistory,
  News,
  Job,
  Referral,
  AffiliateLink,
  DashboardStats,
  UserProfile,
  Learning,
} from "@/types/models";

const normalizeAffiliate = (item: any): AffiliateLink => ({
  ...item,
  show_on_pages: Array.isArray(item?.show_on_pages) ? item.show_on_pages : [],
});

// IPO Services
export const ipoService = {
  async getAll() {
    return apiClient.get<IPO[]>("/ipos");
  },

  async getById(id: string) {
    return apiClient.get<IPO>(`/ipos/${id}`);
  },

  async create(ipo: Partial<IPO>) {
    const { id, ...payload } = ipo;
    return apiClient.post<IPO>("/ipos", { ...payload, ...(id ? { id } : {}) });
  },

  async update(id: string, ipo: Partial<IPO>) {
    const { id: _ignored, ...payload } = ipo;
    return apiClient.put<IPO>(`/ipos/${id}`, payload);
  },

  async delete(id: string) {
    await apiClient.delete<void>(`/ipos/${id}`);
  },

  async getByStatus(status: string) {
    return apiClient.get<IPO[]>("/ipos", { status });
  },
};

// GMP History Services
export const gmpService = {
  async getByIpoId(ipoId: string) {
    return apiClient.get<IpoGmpHistory[]>(`/gmp/ipo/${ipoId}`);
  },

  async create(gmpEntry: Partial<IpoGmpHistory>) {
    return apiClient.post<IpoGmpHistory>("/gmp", gmpEntry);
  },

  async getLatestGmp(ipoId: string) {
    return apiClient.get<IpoGmpHistory[]>(`/gmp/latest/${ipoId}`);
  },

  async getHistory(ipoId: string) {
    return apiClient.get<IpoGmpHistory[]>(`/gmp/ipo/${ipoId}`);
  },

  subscribeToGmpUpdates(callback: (payload: any) => void) {
    const base = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");
    const source = new EventSource(`${base}/realtime/gmp`);

    source.addEventListener("gmp-update", (event) => {
      try {
        const parsed = JSON.parse((event as MessageEvent).data);
        callback(parsed);
      } catch (error) {
        console.error("Failed to parse GMP realtime payload:", error);
      }
    });

    return {
      unsubscribe() {
        source.close();
      },
    };
  },
};

// News Services
export const newsService = {
  async getAll() {
    return apiClient.get<News[]>("/news");
  },

  async getById(id: string) {
    return apiClient.get<News>(`/news/${id}`);
  },

  async create(news: Partial<News>) {
    const { id, ...payload } = news;
    return apiClient.post<News>("/news", { ...payload, ...(id ? { id } : {}) });
  },

  async update(id: string, news: Partial<News>) {
    const { id: _ignored, ...payload } = news;
    return apiClient.put<News>(`/news/${id}`, payload);
  },

  async delete(id: string) {
    await apiClient.delete<void>(`/news/${id}`);
  },

  async getByCategory(category: string) {
    return apiClient.get<News[]>("/news", { category });
  },

  async getAllAdmin() {
    return apiClient.get<News[]>("/news", { admin: true });
  },
};

// Jobs Services
export const jobService = {
  async getAll() {
    return apiClient.get<Job[]>("/jobs");
  },

  async getById(id: string) {
    return apiClient.get<Job>(`/jobs/${id}`);
  },

  async create(job: Partial<Job>) {
    return apiClient.post<Job>("/jobs", job);
  },

  async update(id: string, job: Partial<Job>) {
    return apiClient.put<Job>(`/jobs/${id}`, job);
  },

  async delete(id: string) {
    await apiClient.delete<void>(`/jobs/${id}`);
  },

  async getByCategory(category: string) {
    return apiClient.get<Job[]>("/jobs", { category });
  },

  async getByLocation(location: string) {
    return apiClient.get<Job[]>("/jobs", { location });
  },

  async getAllAdmin() {
    return apiClient.get<Job[]>("/jobs", { admin: true });
  },
};

// Referral Services
export const referralService = {
  async getByUserId(userId: string) {
    return apiClient.get<Referral | null>(`/referrals/user/${userId}`);
  },

  async create(userId: string) {
    try {
      const rpc = await apiClient.post<{ code: string }>("/rpc/generate_referral_code");
      return apiClient.post<Referral>("/referrals", { user_id: userId, referral_code: rpc.code });
    } catch {
      return apiClient.post<Referral>("/referrals", { user_id: userId });
    }
  },

  async update(id: string, referral: Partial<Referral>) {
    return apiClient.put<Referral>(`/referrals/${id}`, referral);
  },

  async getByCode(code: string) {
    return apiClient.get<Referral>(`/referrals/code/${code}`);
  },

  async getAll() {
    return apiClient.get<Referral[]>("/referrals");
  },
};

// Affiliate Links Services
export const affiliateService = {
  async getAll() {
    const rows = await apiClient.get<AffiliateLink[]>("/affiliate-links");
    return rows.map(normalizeAffiliate);
  },

  async create(affiliate: Partial<AffiliateLink>) {
    const row = await apiClient.post<AffiliateLink>("/affiliate-links", affiliate);
    return normalizeAffiliate(row);
  },

  async update(id: string, affiliate: Partial<AffiliateLink>) {
    const row = await apiClient.put<AffiliateLink>(`/affiliate-links/${id}`, affiliate);
    return normalizeAffiliate(row);
  },

  async delete(id: string) {
    await apiClient.delete<void>(`/affiliate-links/${id}`);
  },

  async getAllAdmin() {
    const rows = await apiClient.get<AffiliateLink[]>("/affiliate-links", { admin: true });
    return rows.map(normalizeAffiliate);
  },
};

// Dashboard Stats Services
export const statsService = {
  async get() {
    return apiClient.get<DashboardStats>("/dashboard-stats");
  },

  async update(stats: Partial<DashboardStats>) {
    if (!stats.id) {
      throw new Error("Dashboard stats id is required for update");
    }

    return apiClient.put<DashboardStats>(`/dashboard-stats/${stats.id}`, stats);
  },
};

// User Profile Services
export const profileService = {
  async getByUserId(userId: string) {
    return apiClient.get<UserProfile | null>(`/user-profiles/user/${userId}`);
  },

  async create(profile: Partial<UserProfile>) {
    return apiClient.post<UserProfile>("/user-profiles", profile);
  },

  async update(userId: string, profile: Partial<UserProfile>) {
    return apiClient.put<UserProfile>(`/user-profiles/user/${userId}`, profile);
  },

  async getAll() {
    return apiClient.get<UserProfile[]>("/user-profiles");
  },
};

// Learning Services
export const learningService = {
  async getAll() {
    return apiClient.get<Learning[]>("/learning");
  },

  async getById(id: string) {
    return apiClient.get<Learning>(`/learning/${id}`);
  },

  async create(learning: Partial<Learning>) {
    const { id, ...payload } = learning;
    return apiClient.post<Learning>("/learning", { ...payload, ...(id ? { id } : {}) });
  },

  async update(id: string, learning: Partial<Learning>) {
    const { id: _ignored, ...payload } = learning;
    return apiClient.put<Learning>(`/learning/${id}`, payload);
  },

  async delete(id: string) {
    await apiClient.delete<void>(`/learning/${id}`);
  },
};
